import { Box, Divider, Group, Text, useMantineColorScheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import classes from "./Bar.module.css";

import type { FC, ReactNode } from "react";

export type PerformanceBarProps = {
  align?: "left" | "right";
  label: ReactNode;
  value: number;
  logarithmicValue?: number;
  stretch?: boolean;
  showBar?: boolean;
  isEmptyState?: boolean;
};

const BAR_MAX_WIDTH_DESKTOP = 140;
const BAR_MAX_WIDTH_MOBILE = 40;

export const PerformanceBar: FC<PerformanceBarProps> = ({
  align = "right",
  label,
  value,
  logarithmicValue,
  stretch = false,
  showBar = true,
  isEmptyState = false,
}) => {
  const isMobile = useMediaQuery("(max-width: 549px)");
  const { colorScheme } = useMantineColorScheme();
  const barAlign = align === "right" ? "flex-end" : "flex-start";
  const barStyle =
    align === "right"
      ? {
          borderTopLeftRadius: "var(--mantine-radius-xs)",
          borderBottomLeftRadius: "var(--mantine-radius-xs)",
        }
      : {
          borderTopRightRadius: "var(--mantine-radius-xs)",
          borderBottomRightRadius: "var(--mantine-radius-xs)",
        };

  const isDark = colorScheme === "dark";
  const colorVariant = value >= 0 ? "success" : "danger";
  const background = isEmptyState ? (isDark ? "#4e4f50" : "#D0D8EF") : `primary.${isDark ? 4 : 1}`;

  const barHeight = isMobile ? 25 : 30;
  const barMaxWidth = isMobile ? BAR_MAX_WIDTH_MOBILE : BAR_MAX_WIDTH_DESKTOP;

  const fontSize = { base: "xs", sm: "sm" };

  return (
    <Group
      align="center"
      justify={stretch ? "space-between" : barAlign}
      wrap="nowrap"
      w={stretch ? "100%" : undefined}
    >
      {align === "right" &&
        (typeof label === "string" ? (
          <Text c={colorVariant} fz={fontSize}>
            {label}
          </Text>
        ) : (
          label
        ))}
      <Group gap={0} align="center" wrap="nowrap">
        {showBar && align === "left" && <Divider orientation="vertical" h={38} size="sm" />}
        {showBar && (
          <Box
            bg={background}
            className={classes.bar_performanceItem}
            w={2 + (logarithmicValue ?? value) * (barMaxWidth - 2)}
            maw={barMaxWidth}
            h={barHeight}
            style={barStyle}
          />
        )}
        {showBar && align === "right" && <Divider orientation="vertical" h={38} size="sm" />}
      </Group>
      {align === "left" &&
        (typeof label === "string" ? (
          <Text c={colorVariant} fz={fontSize}>
            {label}
          </Text>
        ) : (
          label
        ))}
    </Group>
  );
};
